import { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import {
  LayoutDashboard,
  Users,
  FileText,
  FolderKanban,
  UserCheck,
  Calendar,
  Share2,
  BarChart3,
  UserCog,
  CheckCircle,
  LogOut,
  Menu,
  X,
  Sun,
  Moon
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { isDark, toggleTheme } = useTheme();

  // TODO: Get user role from auth context/backend
  const userRole = 'Admin'; // This should come from auth context
  
  const isCoreHead = userRole === 'Core Head' || userRole === 'Admin' || userRole === 'Owner';

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/problem-statements', label: 'Problem Statements', icon: FileText },
    { path: '/admin/projects', label: 'Projects', icon: FolderKanban },
    { path: '/admin/applications', label: 'Applications', icon: UserCheck },
    { path: '/admin/project-leaders', label: 'Project Leaders', icon: Users },
    ...(isCoreHead ? [
      { path: '/admin/review-approvals', label: 'Review & Approvals', icon: CheckCircle },
      { path: '/admin/scheduling', label: 'Scheduling', icon: Calendar },
    ] : []),
    { path: '/admin/integrations', label: 'Integrations', icon: Share2 },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/users', label: 'Users', icon: UserCog },
  ];

  const handleLogout = () => {
    // TODO: Integrate with backend logout
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className={`min-h-screen flex transition-colors ${
      isDark ? 'bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#2d1b4e]' : 'bg-gray-50'
    }`}>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`transition-all duration-300 ${
          sidebarOpen ? 'w-64 translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-20'
        } fixed h-screen z-40 ${
          isDark 
            ? 'bg-gradient-to-b from-[#1a0b2e] to-[#2d1b4e] border-r border-purple-500/20' 
            : 'bg-white border-r border-gray-200 shadow-lg'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo/Title */}
          <div className={`p-6 border-b ${
            isDark ? 'border-purple-500/20' : 'border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <h2 className={`text-xl font-bold transition-opacity ${
                sidebarOpen ? 'opacity-100' : 'opacity-0'
              } ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {sidebarOpen && 'Admin Panel'}
              </h2>
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition ${
                  isDark 
                    ? 'hover:bg-purple-500/20 text-purple-300' 
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link key={item.path} to={item.path}>
                  <div
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      active
                        ? isDark
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                          : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                        : isDark
                          ? 'text-purple-200 hover:bg-purple-500/20'
                          : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={20} className="flex-shrink-0" />
                    {sidebarOpen && (
                      <span className="font-medium">
                        {item.label}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className={`p-4 border-t ${
            isDark ? 'border-purple-500/20' : 'border-gray-200'
          }`}>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className={`w-full justify-start ${
                isDark
                  ? 'text-purple-200 hover:bg-purple-500/20 hover:text-purple-300'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <LogOut size={20} className="flex-shrink-0" />
              {sidebarOpen && (
                <span className="ml-3">
                  Logout
                </span>
              )}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 lg:ml-0 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        {/* Header */}
        <header className={`border-b px-4 sm:px-6 py-4 shadow-sm transition-colors ${
          isDark 
            ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-500/20' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`lg:hidden p-2 rounded-lg transition-colors ${
                  isDark 
                    ? 'hover:bg-purple-500/20 text-purple-300' 
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <Menu size={20} />
              </button>
              <h1 className={`text-xl sm:text-2xl font-semibold transition-colors ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {menuItems.find(item => isActive(item.path))?.label || 'Admin Panel'}
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <span className={`hidden sm:inline ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Welcome, Admin</span>
              
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${
                  isDark 
                    ? 'bg-purple-500/20 hover:bg-purple-500/30 text-yellow-400 border border-purple-500/30' 
                    : 'bg-gray-100 hover:bg-gray-200 text-purple-600'
                }`}
                title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;

